import { CacheEntry } from '@/types/currency'
import { API_CONFIG } from '@/utils/constants'
import { safeStorage, safeJsonParse } from '@/utils/helpers'

/**
 * Service for managing cached data with TTL and storage persistence
 */
export class CacheService {
  private memoryCache: Map<string, CacheEntry<any>> = new Map()
  private readonly STORAGE_PREFIX = 'cc_cache_'

  /**
   * Get cached data by key
   */
  get<T>(key: string): CacheEntry<T> | null {
    // Check memory cache first
    const memoryEntry = this.memoryCache.get(key)
    if (memoryEntry && this.isValidEntry(memoryEntry)) {
      return memoryEntry as CacheEntry<T>
    }

    // Check persistent storage
    const storageKey = this.STORAGE_PREFIX + key
    const storedData = safeStorage.getItem(storageKey)
    
    if (storedData) {
      const entry: CacheEntry<T> = safeJsonParse(storedData, null)
      if (entry && this.isValidEntry(entry)) {
        // Restore to memory cache
        this.memoryCache.set(key, entry)
        return entry
      } else {
        // Remove invalid entry from storage
        safeStorage.removeItem(storageKey)
      }
    }

    return null
  }

  /**
   * Set cached data with TTL
   */
  set<T>(key: string, data: T, ttl: number = API_CONFIG.CACHE_TTL.EXCHANGE_RATES): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
      source: 'cache'
    }

    // Store in memory cache
    this.memoryCache.set(key, entry)

    // Store in persistent storage
    const storageKey = this.STORAGE_PREFIX + key
    try {
      safeStorage.setItem(storageKey, JSON.stringify(entry))
    } catch (error) {
      console.warn('Failed to persist cache to storage:', error)
    }
  }

  /**
   * Remove cached data by key
   */
  delete(key: string): boolean {
    // Remove from memory cache
    const memoryResult = this.memoryCache.delete(key)

    // Remove from persistent storage
    const storageKey = this.STORAGE_PREFIX + key
    const storageResult = safeStorage.removeItem(storageKey)

    return memoryResult || storageResult
  }

  /**
   * Clear all cached data
   */
  clear(): void {
    // Clear memory cache
    this.memoryCache.clear()

    // Clear persistent storage (all cache entries)
    if (typeof window !== 'undefined' && window.localStorage) {
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.startsWith(this.STORAGE_PREFIX)) {
          safeStorage.removeItem(key)
        }
      })
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    memoryEntries: number
    storageEntries: number
    totalSize: number
    validEntries: number
    expiredEntries: number
  } {
    let storageEntries = 0
    let totalSize = 0
    let validEntries = 0
    let expiredEntries = 0

    // Count memory cache entries
    this.memoryCache.forEach(entry => {
      if (this.isValidEntry(entry)) {
        validEntries++
      } else {
        expiredEntries++
      }
    })

    // Count storage cache entries
    if (typeof window !== 'undefined' && window.localStorage) {
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.startsWith(this.STORAGE_PREFIX)) {
          storageEntries++
          const value = localStorage.getItem(key)
          if (value) {
            totalSize += value.length
            const entry = safeJsonParse(value, null)
            if (entry) {
              if (this.isValidEntry(entry)) {
                validEntries++
              } else {
                expiredEntries++
              }
            }
          }
        }
      })
    }

    return {
      memoryEntries: this.memoryCache.size,
      storageEntries,
      totalSize,
      validEntries,
      expiredEntries
    }
  }

  /**
   * Clean up expired entries
   */
  cleanup(): number {
    let removedCount = 0

    // Clean memory cache
    const memoryKeysToRemove: string[] = []
    this.memoryCache.forEach((entry, key) => {
      if (!this.isValidEntry(entry)) {
        memoryKeysToRemove.push(key)
      }
    })

    memoryKeysToRemove.forEach(key => {
      this.memoryCache.delete(key)
      removedCount++
    })

    // Clean persistent storage
    if (typeof window !== 'undefined' && window.localStorage) {
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.startsWith(this.STORAGE_PREFIX)) {
          const value = localStorage.getItem(key)
          if (value) {
            const entry = safeJsonParse(value, null)
            if (!entry || !this.isValidEntry(entry)) {
              safeStorage.removeItem(key)
              removedCount++
            }
          }
        }
      })
    }

    return removedCount
  }

  /**
   * Check if cache entry is still valid based on TTL
   */
  private isValidEntry(entry: CacheEntry<any>): boolean {
    if (!entry || !entry.timestamp || !entry.ttl) {
      return false
    }

    const now = Date.now()
    return (now - entry.timestamp) < entry.ttl
  }

  /**
   * Get cache key for exchange rate
   */
  static getRateKey(fromCurrency: string, toCurrency: string): string {
    return `rate_${fromCurrency}_${toCurrency}`
  }

  /**
   * Get cache key for historical data
   */
  static getHistoricalKey(
    fromCurrency: string,
    toCurrency: string,
    startDate: Date,
    endDate: Date,
    interval: string
  ): string {
    const start = startDate.toISOString().split('T')[0]
    const end = endDate.toISOString().split('T')[0]
    return `historical_${fromCurrency}_${toCurrency}_${start}_${end}_${interval}`
  }

  /**
   * Get cache key for currency list
   */
  static getCurrencyListKey(): string {
    return 'currency_list'
  }

  /**
   * Preload frequently used currency pairs
   */
  async preloadPopularPairs(pairs: Array<[string, string]>): Promise<void> {
    // This would be implemented to fetch and cache popular currency pairs
    // during app initialization for better performance
    console.log('Preloading popular currency pairs:', pairs)
  }

  /**
   * Set cache size limit and implement LRU eviction
   */
  private enforceSizeLimit(): void {
    const MAX_MEMORY_ENTRIES = 100
    
    if (this.memoryCache.size > MAX_MEMORY_ENTRIES) {
      // Convert to array and sort by timestamp (oldest first)
      const entries = Array.from(this.memoryCache.entries()).sort(
        ([, a], [, b]) => a.timestamp - b.timestamp
      )
      
      // Remove oldest entries until we're under the limit
      const entriesToRemove = entries.slice(0, entries.length - MAX_MEMORY_ENTRIES)
      entriesToRemove.forEach(([key]) => {
        this.memoryCache.delete(key)
      })
    }
  }
}